import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { formatGBP, relDate, MEDIA_TONES } from "../lib/format";
import { toast } from "sonner";
import {
  Download, Share2, Camera, PlayCircle, BarChart3, Handshake, Check, AlertCircle, Clock, Eye, Sparkles
} from "lucide-react";
import { KatlasRecord, KatlasModeChip, PolicyGatePassedChip, ReceiptSignedChip } from "../components/Katlas";

const VERIFIED = {
  verified: { Icon: Check, label: "Verified", color: "var(--rag-green-text)", bg: "var(--rag-green-bg)" },
  pending: { Icon: Clock, label: "Pending", color: "var(--rag-amber-text)", bg: "var(--rag-amber-bg)" },
  incomplete: { Icon: AlertCircle, label: "Incomplete", color: "var(--rag-red-text)", bg: "var(--rag-red-bg)" },
};

const SEV_STYLES = {
  high: { color: "var(--rag-red-text)", bg: "var(--rag-red-bg)" },
  medium: { color: "var(--rag-amber-text)", bg: "var(--rag-amber-bg)" },
  low: { color: "#4F5052", bg: "#EEEEF1" },
};

export default function MatchReceipt({ publicMode = false }) {
  const { id, token } = useParams();
  const [m, setM] = useState(null);

  useEffect(() => {
    (publicMode ? api.publicMatch(token) : api.getMatch(id)).then(setM);
  }, [id, token, publicMode]);

  if (!m) return <Card className="p-8 text-sm text-muted-foreground">Loading match…</Card>;

  const p = m.payload || {};
  const d = p.driver_snapshot || {};
  const s = p.sponsor_snapshot || {};
  const a = p.alignment || {};
  const ai = p.ai_summary || {};
  const shared = p.shared_assets || [];
  const withheld = p.withheld_assets || [];
  const v = VERIFIED[s.verified_status] || VERIFIED.incomplete;

  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/share/match/${m.share_token}`);
    toast.success("Shareable link copied");
  };
  const downloadJSON = () => {
    const blob = new Blob([JSON.stringify(m, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a2 = document.createElement("a");
    a2.href = url; a2.download = `${m.id}.json`; a2.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={publicMode ? "min-h-screen bg-background" : ""} data-testid={publicMode ? "public-match" : "match-receipt-page"}>
      {publicMode && (
        <header className="border-b border-border bg-white">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="font-semibold">ClubCierge · Introduction record</div>
            <span className="text-[10px] tracking-eyebrow font-mono-ui px-2 py-0.5 rounded border border-border">Read-only · {m.id}</span>
          </div>
        </header>
      )}

      <div className={`${publicMode ? "max-w-4xl mx-auto px-6 py-10" : ""} space-y-6`}>
        {!publicMode && (
          <Link to={`/sponsors/${s.id}`} className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-sponsor">
            ← Back to sponsor
          </Link>
        )}

        {/* Header */}
        <section className="relative overflow-hidden rounded-2xl border border-border bg-white">
          <div className="checker-stripe absolute top-0 right-0 w-64 h-64 pointer-events-none" />
          <div className="relative grid lg:grid-cols-5 gap-6 p-8">
            <div className="lg:col-span-3 space-y-4">
              <div className="flex items-center gap-2 tracking-eyebrow text-muted-foreground">
                <Handshake className="h-3.5 w-3.5" /> Approved introduction
              </div>
              <h1 className="text-3xl sm:text-4xl tracking-tight font-semibold leading-tight">
                {d.name} <span className="text-muted-foreground">×</span> {s.name}
              </h1>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="inline-flex items-center gap-1 font-mono-ui px-2 py-1 rounded" style={{ color: v.color, backgroundColor: v.bg }}>
                  <v.Icon className="h-3 w-3" /> {v.label}
                </span>
                <span className="font-mono-ui text-muted-foreground">{s.type_label}</span>
                <KatlasModeChip />
                <PolicyGatePassedChip passed />
                <ReceiptSignedChip />
              </div>
              {ai.why_match && <p className="text-[15px] mt-2 max-w-2xl leading-relaxed">{ai.why_match}</p>}
            </div>
            <div className="lg:col-span-2 flex flex-col items-start lg:items-end gap-2">
              <div className="text-right text-xs text-muted-foreground">
                Approved by <b className="text-foreground">{p.approved_by}</b><br />
                {p.approved_role} · {relDate(p.approved_at)}
              </div>
              <div className="text-right">
                <div className="tracking-eyebrow text-muted-foreground">Alignment</div>
                <div className="text-2xl font-semibold font-mono-ui">{a.alignment_score}%</div>
              </div>
            </div>
          </div>
        </section>

        {/* Driver + Sponsor side-by-side */}
        <section className="grid lg:grid-cols-2 gap-5">
          <Card className="p-6 border-border race-card">
            <div className="tracking-eyebrow text-muted-foreground">Driver evidence used</div>
            <div className="flex items-start gap-4 mt-3">
              <div className="relative">
                <img src={d.avatar_url} alt="" className="h-16 w-16 rounded-md object-cover bg-muted" />
                <span className="absolute -bottom-1 -right-1 h-7 min-w-7 px-1 rounded-md bg-[#F25E1D] text-white text-[11px] font-semibold grid place-items-center font-mono-ui">
                  #{d.race_number}
                </span>
              </div>
              <div className="min-w-0">
                <div className="font-semibold">{d.name}</div>
                <div className="text-xs text-muted-foreground">{d.series} · {d.age_band}</div>
                <div className="text-xs text-muted-foreground font-mono-ui">{d.location}</div>
              </div>
            </div>
            {d.headline && <p className="text-sm mt-3 leading-relaxed">{d.headline}</p>}
            <div className="mt-3 text-xs text-muted-foreground">
              Funding gap at time of intro: <b className="text-foreground">{formatGBP((d.funding_need || {}).gap)}</b>
            </div>
          </Card>

          <Card className="p-6 border-border race-card">
            <div className="tracking-eyebrow text-muted-foreground">Sponsor credentials</div>
            <div className="flex items-start gap-4 mt-3">
              <div className="h-14 w-14 rounded-md grid place-items-center text-white font-semibold font-mono-ui shrink-0" style={{ backgroundColor: s.logo_color }}>{s.logo_initials}</div>
              <div className="min-w-0">
                <div className="font-semibold">{s.name}</div>
                <div className="text-xs text-muted-foreground">{s.type_label}</div>
                {s.rep_name && <div className="text-xs text-muted-foreground font-mono-ui">Rep: {s.rep_name}</div>}
              </div>
            </div>
            <div className="text-xs mt-3 space-y-1">
              <div><b>Aims:</b> {(s.aims || []).join(", ")}</div>
              <div><b>Support:</b> {(s.support_offered || []).join(", ")}</div>
              {s.terms_summary && <div className="text-muted-foreground">{s.terms_summary}</div>}
            </div>
          </Card>
        </section>

        {/* Alignment + flags */}
        <section className="grid md:grid-cols-2 gap-4">
          <Card className="p-5 border-border race-card">
            <div className="tracking-eyebrow text-muted-foreground">Alignment points</div>
            <ul className="mt-2 space-y-1.5 text-sm">
              {(a.alignment_points || []).map((pt, i) => <li key={i} className="flex gap-2"><Check className="h-3.5 w-3.5 mt-0.5 text-[var(--rag-green-text)]" /><span>{pt}</span></li>)}
            </ul>
          </Card>
          <Card className="p-5 border-border race-card">
            <div className="tracking-eyebrow text-muted-foreground">Gaps reviewed</div>
            <ul className="mt-2 space-y-2 text-sm">
              {(a.flags || []).length === 0 && <li className="text-sm text-muted-foreground">No automatic gaps flagged at time of approval.</li>}
              {(a.flags || []).map((f, i) => {
                const st = SEV_STYLES[f.severity] || SEV_STYLES.low;
                return (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-[10px] tracking-eyebrow font-mono-ui px-1.5 py-0.5 rounded mt-0.5 shrink-0" style={{ color: st.color, backgroundColor: st.bg }}>{f.severity}</span>
                    <span>{f.label}</span>
                  </li>
                );
              })}
            </ul>
          </Card>
        </section>

        {/* Shared / withheld assets */}
        <Card className="p-6 border-border race-card">
          <div className="flex items-center justify-between">
            <div className="tracking-eyebrow text-muted-foreground">What was shared</div>
            <span className="text-[11px] text-muted-foreground">{shared.length} shared · {withheld.length} withheld</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
            {shared.map((mm, i) => (
              <div key={i} className={`relative rounded-lg overflow-hidden bg-gradient-to-br ${MEDIA_TONES[mm.tone] || MEDIA_TONES.paddock} aspect-[4/3]`}>
                <div className="absolute inset-0 grid place-items-center">
                  {mm.kind === "reel" ? <PlayCircle className="h-9 w-9 opacity-80" /> : mm.kind === "stat" ? <BarChart3 className="h-9 w-9 opacity-80" /> : <Camera className="h-9 w-9 opacity-80" />}
                </div>
                <div className="absolute top-2 left-2 text-[10px] tracking-eyebrow font-mono-ui bg-black/30 backdrop-blur px-1.5 py-0.5 rounded">{mm.badge}</div>
                <div className="absolute bottom-2 left-2 right-2 text-[12px] font-medium drop-shadow">{mm.title}</div>
                <div className="absolute top-2 right-2 text-[10px] tracking-eyebrow font-mono-ui bg-[var(--rag-green-bg)] text-[var(--rag-green-text)] px-1.5 py-0.5 rounded inline-flex items-center gap-0.5"><Eye className="h-2.5 w-2.5" /> SHARED</div>
              </div>
            ))}
          </div>
          {withheld.length > 0 && (
            <div className="mt-4 text-xs text-muted-foreground">
              <b className="text-foreground">Withheld:</b> {withheld.map((w) => w.title).join(" · ")}
            </div>
          )}
          {p.custom_notes && <div className="text-xs text-muted-foreground mt-3 italic">Officer note: “{p.custom_notes}”</div>}
        </Card>

        {/* KATLAS governance record */}
        <KatlasRecord
          hash={m.hash}
          receiptId={m.id}
          policyGate="passed"
          actor={`${p.approved_by} (${p.approved_role})`}
          action="Approved sponsor introduction"
          shared={[`${shared.length} asset(s) shared`]}
          withheld={withheld.length ? [`${withheld.length} asset(s) withheld`] : []}
          attestation={m.katlas_attestation || null}
          timestamp={m.approved_at || null}
          shareToken={m.share_token || null}
          car={{
            custody: `${d?.name || "Driver"}'s talent wallet controls performance evidence, sponsor-readiness and the shared / withheld asset selection.`,
            authority: `${p.approved_role || "Approving officer"} approved the sponsor introduction under policy gate CAR-CLUBCIERGE-001.`,
            receipt: m.katlas_attestation?.mode === "live"
              ? `KATLAS signed the approval at ${m.katlas_attestation.node_timestamp || m.approved_at}, producing receipt hash ${m.katlas_attestation.attestation_hash || m.hash}.`
              : `KATLAS governance simulation produced receipt hash ${m.hash} at ${m.approved_at}. A live node would attest the same action.`,
          }}
        />

        {/* Receipt footer */}
        <Card className="p-6 border-border race-card receipt-paper font-mono-ui text-xs space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="tracking-eyebrow text-muted-foreground">Match ID</div>
              <div data-testid="match-id">{m.id}</div>
            </div>
            <div>
              <div className="tracking-eyebrow text-muted-foreground">Hash</div>
              <div data-testid="match-hash">{m.hash}</div>
            </div>
          </div>
          {m.share_token && (
            <div className="text-[11px] text-muted-foreground font-mono-ui break-all" data-testid="match-share-token">
              <span className="tracking-eyebrow not-italic">Share token · </span>
              {m.share_token}
            </div>
          )}
          <div className="dashed-divider" />
          <div style={{ fontFamily: "'IBM Plex Sans', sans-serif" }} className="text-[11px] text-muted-foreground leading-relaxed">
            Drivers prove performance. Sponsors prove intent. This record shows which evidence was used, which credentials were checked,
            which permissions were applied and who authorised the introduction.
          </div>
        </Card>

        {!publicMode && (
          <div className="flex flex-wrap gap-2 justify-end">
            <Button variant="outline" onClick={copyLink} data-testid="copy-match-link"><Share2 className="h-4 w-4 mr-1.5" /> Copy shareable link</Button>
            <Button onClick={downloadJSON} data-testid="download-match-json"><Download className="h-4 w-4 mr-1.5" /> Download JSON</Button>
          </div>
        )}

        {publicMode && (
          <div className="text-center text-[11px] text-muted-foreground py-4">
            Shareable read-only introduction record · ClubCierge · AI recommends · Authorised humans decide · The club keeps the evidence
          </div>
        )}
      </div>
    </div>
  );
}
